"use client";

import { useRef } from "react";
import { TimelineMediaFile } from "@/lib/timeline";
import { useMediaManager } from "@/hooks/useMediaManager";

type Props = {
  open: boolean;
  file?: TimelineMediaFile | null;
  onClose: () => void;
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  onVideoPlay?: () => void;
};

function getFullUrl(file: TimelineMediaFile) {
  return file.download_url || file.url;
}

function getPosterUrl(file: TimelineMediaFile) {
  return (
    file.poster?.url ||
    file.cover?.url ||
    file.thumbnail?.url ||
    file.preview_image?.url ||
    undefined
  );
}

export default function Lightbox({
  open,
  file,
  onClose,
  videoRef,
  onVideoPlay,
}: Props) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const { registerMedia } = useMediaManager();

  if (!open || !file) return null;

  const fullUrl = getFullUrl(file);
  const posterUrl = getPosterUrl(file);

  const handleAudioPlay = () => {
    if (audioRef.current) {
      registerMedia(audioRef.current);
    }
  };

  const handleVideoPlay = () => {
    if (videoRef?.current) {
      registerMedia(videoRef.current);
    }
    onVideoPlay?.();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.86)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.25rem",
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close media preview"
        style={{
          position: "fixed",
          top: 18,
          right: 18,
          width: 42,
          height: 42,
          borderRadius: "999px",
          border: "1px solid rgba(255,255,255,0.22)",
          background: "rgba(0,0,0,0.35)",
          color: "#fff",
          fontSize: 24,
          cursor: "pointer",
          zIndex: 1001,
        }}
      >
        ×
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "min(1100px, 100%)",
          maxHeight: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {file.media_kind === "image" && (
          <img
            src={fullUrl}
            alt={file.file_name || ""}
            style={{
              maxWidth: "100%",
              maxHeight: "92vh",
              objectFit: "contain",
              borderRadius: 14,
              boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
            }}
          />
        )}

        {file.media_kind === "video" && (
          <video
            ref={videoRef}
            controls
            autoPlay
            playsInline
            poster={posterUrl}
            onPlay={handleVideoPlay}
            style={{
              width: "100%",
              maxHeight: "92vh",
              borderRadius: 14,
              background: "#000",
              boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
            }}
          >
            <source src={fullUrl} />
          </video>
        )}

        {file.media_kind === "audio" && (
          <div
            style={{
              width: "min(520px, 100%)",
              borderRadius: 18,
              padding: "1.25rem",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.14)",
              color: "#fff",
              backdropFilter: "blur(14px)",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: "1rem" }}>
              {file.file_name || "Audio"}
            </div>
            <audio
              ref={audioRef}
              controls
              autoPlay
              onPlay={handleAudioPlay}
              style={{ width: "100%" }}
            >
              <source src={fullUrl} />
            </audio>
          </div>
        )}

        {file.media_kind === "file" && (
          <a
            href={fullUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              width: "min(420px, 100%)",
              borderRadius: 18,
              padding: "1.25rem",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.14)",
              color: "#fff",
              textDecoration: "none",
              backdropFilter: "blur(14px)",
            }}
          >
            Open {file.file_name || "file"}
          </a>
        )}
      </div>
    </div>
  );
}
